import InterviewCard from '@components/InterviewCard/InterviewCard.component'
import {
  Box,
  Button,
  CircularProgress,
  styled,
  TextareaAutosize,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import axios from 'axios'
import { useState } from 'react'
import NoData from 'icons/NoData.icon'

export const Route = createFileRoute('/(dashboard)/_dashboard/job-description')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      technologyId: search.technologyId as string,
    }
  },
  component: RouteComponent,
})

const StyledContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '48px 56px',
  background: '#ffffff',
  borderRadius: '24px',
  boxShadow: '0px 1px 2px 0px #0205111A',
  width: '100%',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    padding: '24px',
  },
}))

const StyledTextArea = styled(TextareaAutosize)(() => ({
  width: '100%',
  minHeight: '200px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--Interviewer-Lightest-Black)',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '20px',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
  },
}))

function RouteComponent() {
  const { technologyId } = useSearch({ from: '/(dashboard)/_dashboard/job-description' });
  const [jobDescription, setJobDescription] = useState('');
  const navigate = useNavigate();

  const {data} = useQuery({
    queryKey: ['job-description', technologyId],
    queryFn: async () => {
      const token = localStorage.getItem('token');

      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/main?technologyId=${technologyId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      return response.data;
    },
    enabled: !!technologyId,
  })

  const handleNext = () => {
    mutation.mutate();
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `https://interviewer.runasp.net/api/technologies/${technologyId}/categories/scale`,
        {
          jobDescription: jobDescription
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log(data);
      navigate({
        to: '/customize-interview',
        search: { technologyId },
        state: { categories: data } as any
      });
    },
    onError: (error) => {
      console.log(error);
      return <NoData />;
    },
  });

  if (mutation.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: {xs: '100%', md: '80%'},
      mx: 'auto',
      padding: {xs: '20px 16px', md: '125px 75px'}
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Typography variant="h1" sx={{ textAlign: 'center', mb: '32px' }}>
          Tell us about the job you are applying for
        </Typography>

        <StyledContainer>
          <Typography variant="h3" sx={{ pb: '8px', fontSize: '16px' }}>
            Job description
          </Typography>
          <StyledTextArea 
            value={jobDescription} 
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Describe the job you’re applying for..." 
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: '32px' }}
            onClick={handleNext}
          >
            Next
          </Button>
        </StyledContainer>

        <Box sx={{ pt: '56px', width: '100%', px: {xs:'0px', md:'56px'} }}>
          <Typography variant="h4" sx={{ pb: '12px', fontWeight: '600', color: 'var(--Interviewer-Lightest-Black)' }}>
            Here for training?
          </Typography>
          <Typography variant="h2" sx={{ pb: '32px' }}>
            Try our predefined job interviews
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {data?.map((interview: any, index: number) => (
              <InterviewCard
                key={index}
                id={interview.id}
                title={interview.name}
                difficulty={interview.difficulty}
                description={interview.description}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
